import { Botao, Cartao, EstadoCarregando, EstadoVazio, Etiqueta, useAvisos } from '@baiocchi/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { chaves } from '../api/chaves'
import { notificacoesApi } from '../api/catalogos'
import { mensagemDoErro } from '../api/erros'
import { useSessao } from '../autenticacao/contexto-sessao'
import { EstadoDeErro } from '../componentes/estados-tela'
import { CabecalhoPagina } from '../layout/layout-app'
import { formatarTempoRelativo } from '../utilitarios/formatar'

/** Rota `/sair`: efetua o logout e devolve ao login. */
export function PaginaSair() {
  const { sair } = useSessao()
  const navegar = useNavigate()
  /**
   * Guarda de execução única.
   *
   * `useEffect` sem guarda roda **duas vezes** sob `StrictMode` (que `main.tsx`
   * liga), e cada execução dispara um `POST /auth/logout`. O efeito visível eram
   * dois `LOGOUT_REALIZADO` por saída no log de auditoria, com o mesmo
   * `familiaId` e 1 ms de diferença — ruído num registro que existe justamente
   * para ser confiável (RN-32).
   *
   * O efeito invisível era pior: a segunda chamada é assíncrona e pode chegar
   * DEPOIS de um login seguinte, revogando a família de refresh recém-criada e
   * derrubando a sessão nova. Foi assim que apareceu — o roteiro de demonstração
   * troca de usuário várias vezes e caía de volta na tela de login.
   *
   * `useRef` e não `useState`: mudar estado aqui provocaria outro render, que é o
   * que se quer evitar. Encontrado em 12/08/2026, ver `17-demo-e2e.md`.
   */
  const jaEncerrou = useRef(false)

  useEffect(() => {
    if (jaEncerrou.current) return
    jaEncerrou.current = true
    void sair().finally(() => navegar('/entrar', { replace: true }))
  }, [sair, navegar])

  return <EstadoCarregando descricao="Encerrando sua sessão…" />
}

/** Rota inexistente. */
export function PaginaNaoEncontrada() {
  const navegar = useNavigate()

  return (
    <Cartao className="mx-auto max-w-lg">
      <EstadoVazio
        titulo="Página não encontrada"
        descricao="O endereço não existe neste sistema. Se você chegou por um link antigo, ele pode ter mudado."
        acao={<Botao onClick={() => navegar('/')}>Ir para o início</Botao>}
      />
    </Cartao>
  )
}

/**
 * Notificações in-app (§11).
 *
 * MVP é polling do contador. As linhas são gravadas pela API na mesma transação das
 * operações de lead (`LEAD_ATRIBUIDO`, `LEAD_SEM_RESPONSAVEL`, `AGENDAMENTO_PROXIMO`)
 * e lidas por `GET /notificacoes`, publicado com o módulo do INT-06.
 */
export function PaginaNotificacoes() {
  const clienteConsulta = useQueryClient()
  const avisos = useAvisos()

  const consulta = useQuery({
    queryKey: chaves.notificacoes.todos,
    queryFn: ({ signal }) => notificacoesApi.listar({ limite: 50 }, signal),
    retry: false,
  })

  const marcarTodas = useMutation({
    mutationFn: () => notificacoesApi.marcarTodasLidas(),
    onSuccess: () => void clienteConsulta.invalidateQueries({ queryKey: chaves.notificacoes.todos }),
    // Sem isto, falhar era indistinguível de "não havia nada para marcar": a lista
    // continuava igual e nenhuma mensagem aparecia.
    onError: (erro) => avisos.erro('Não foi possível marcar como lidas', mensagemDoErro(erro)),
  })

  return (
    <>
      <CabecalhoPagina
        titulo="Notificações"
        descricao="Avisos de lead atribuído, lead sem responsável e compromisso próximo."
        acoes={
          <Botao
            variante="contorno"
            onClick={() => marcarTodas.mutate()}
            carregando={marcarTodas.isPending}
            disabled={consulta.isError || (consulta.data?.dados.length ?? 0) === 0}
          >
            Marcar todas como lidas
          </Botao>
        }
      />

      <Cartao semPadding>
        {consulta.isPending ? (
          <EstadoCarregando />
        ) : consulta.isError ? (
          <EstadoDeErro
            erro={consulta.error}
            aoTentarNovamente={() => void consulta.refetch()}
          />
        ) : (consulta.data?.dados.length ?? 0) === 0 ? (
          <EstadoVazio
            titulo="Nenhuma notificação"
            descricao="Você será avisado quando um lead for atribuído a você ou quando houver compromisso próximo."
          />
        ) : (
          <ul className="divide-y divide-borda">
            {consulta.data?.dados.map((notificacao) => (
              <li key={notificacao.uuid} className="flex items-start gap-3 px-4 py-3 sm:px-5">
                {notificacao.lidaEm === null ? (
                  <span
                    aria-label="Não lida"
                    className="mt-2 h-2 w-2 shrink-0 rounded-full bg-marca-500"
                  />
                ) : (
                  <span aria-hidden="true" className="mt-2 h-2 w-2 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-conteudo">{notificacao.titulo}</p>
                  {notificacao.mensagem ? (
                    <p className="mt-0.5 text-sm text-conteudo-suave">{notificacao.mensagem}</p>
                  ) : null}
                  <p className="mt-1 flex items-center gap-2 text-xs text-conteudo-fraco">
                    <Etiqueta tom="neutro">{notificacao.tipo}</Etiqueta>
                    {formatarTempoRelativo(notificacao.criadoEm)}
                  </p>
                </div>
                {notificacao.leadUuid ? (
                  <Link
                    to={`/leads/${notificacao.leadUuid}`}
                    className="shrink-0 text-xs font-medium text-marca-600 hover:underline"
                  >
                    Abrir lead
                  </Link>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Cartao>
    </>
  )
}
